import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import { useMediaQuery } from 'react-responsive';
import styled from 'styled-components';
import { Rate, Tag } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCommentLines } from '@fortawesome/pro-regular-svg-icons';

import style from 'style';
import { ProviderAPI, useGetApi } from 'providerSite/api';
import defaultLogo from 'assets/physiotherapist-and-boy-holding-ball-455x256.png';

import { getRatingValue } from 'lib/util';
import QualifiedServiceIcon from '../../../components/QualifiedServiceIcon';


const PROVIDER_DATA_INTERVAL = 1000 * 20;    // 20 seconds
const REVIEW_DATA_INTERVAL = 1000 * 3;     // 3 seconds


const Container = styled.div`
  border-radius: ${style.borderRadius.large};
  border: 1px solid ${style.colors.lightBorder};
  box-shadow: ${style.glowingShadow(20)};
  margin-top: 40px;
  color: black;
  position: relative;
  cursor: pointer;
  
  @media (min-width: ${style.sizes.medium}) {
    position: sticky;
    top: 40px;
  }
  
  :hover {
    border-color: ${style.colors.palette.squash};
  }
  
  .sponsored {
    position: absolute;
    top: -25px;
    left: 0;
    color: ${style.colors.palette.squash};
    font-size: 12px;
    font-weight: 400;
    text-shadow: 1px 1px #e8e8e8;
    cursor: default;
    border: 1px solid ${style.colors.palette.squash};
    padding: 0 4px;
    border-radius: 6px;
  }

  .service-container {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
  }

  .service-tag {
    display: flex;
    align-items: center;
    justify-content: center;
    column-gap: 5px;
    font-size: 10px;
    border: 1px solid #dcd2b5;
    border-radius: 8px;
    padding: 2px 5px;
    background-color: #fdfcf9;
    color: #72694f;
    flex-grow: 1;
  }

  .icon-container {
    background: #f1f1f1;
    display: flex;
    justify-content: center;
    align-items: center;
    height: 20px;
    width: 20px;
    border-radius: 6px;
  }

  .icon {
    font-size: 12px;
  }

`;

const LogoContainer = styled.div`
  overflow: hidden;
  border-top-left-radius: ${style.borderRadius.large};
  border-top-right-radius: ${style.borderRadius.large};
  
  .logo {
    width: 100%;
    object-fit: contain;
  }
`;

const DetailsContainer = styled.div`
  padding: 10px 10px;
  display: flex;
  flex-direction: column;
  align-items: center;
  overflow: hidden;
  
  @media (max-width: ${style.sizes.large}) {
    align-items: center;
  }
`;

const Top = styled.div`
  display: flex;
  flex-direction: column;
  column-gap: 20px;
  
  @media (max-width: ${style.sizes.large}) {
    align-items: center;
    margin-bottom: 10px;
  }
  
  .title {
    font-size: 20px;
    font-weight: 700;
  }

`;

const Bottom = styled.div`
  display: flex;
  column-gap: 20px;
  align-items: center;
  
  .rating {
    display: flex;
    align-items: center;
    column-gap: 10px;
    
    @media (min-width: ${style.screens.tab}) and (max-width: ${style.sizes.large}) {
      flex-direction: column;
    }
  }
  
  .rating-text {
    font-weight: 700;
  }
  
  .reviews {
    font-size: 13px;
    display: flex;
    column-gap: 5px;
    align-items: center;
    cursor: pointer;
    font-weight: 500;
    
    @media (min-width: ${style.screens.tab}) and (max-width: ${style.sizes.large}) {
      flex-direction: column;
    }
  }
  
  .review-icon {
    margin-bottom: 3px;
    color: ${style.colors.palette.dustyOrange};
    font-size: 14px;
  }
`;


const SpotlightPremium = () => {

  const { data: _dataList } = useGetApi(ProviderAPI.GET_SPOTLIGHT_PREMIUM_PROVIDERS, {
    onError: err => console.error(err),   // eslint-disable-line no-console
  });

  const dataList = _dataList || [];
  const [currentIndex, setCurrentIndex] = useState(0);

  // Automatically move to the next item
  useEffect(() => {
    if (!dataList.length) return;

    const interval = setInterval(() => {
      setCurrentIndex(prevIndex =>
        prevIndex === dataList.length - 1 ? 0 : prevIndex + 1
      );
    }, PROVIDER_DATA_INTERVAL);

    return () => clearInterval(interval); // cleanup
  }, [dataList]);

  const data = dataList[currentIndex] || {};

  const logoWidth = 300;
  const logoHeight = 175;

  const logoSize = `@${logoWidth}x${logoHeight}`;
  const logo = data.imagePath ? data.imagePath + logoSize : defaultLogo;

  // Not mobile screen. Mobile screen has a bigger card because the layout is flex column
  const isSmallScreen = useMediaQuery({minWidth: style.screens.tab, maxWidth: style.sizes.large});

  if (!Object.keys(data).length) return;

  return (
    <Container key={data.uuid} onClick={() => window.open(data.url, '_blank')}>
      <div className="sponsored">Featured Partner</div>
      <LogoContainer>
        <img
          src={logo}
          alt={`Provider ${data.name} logo`}
          className="logo"
          aria-label="Provider logo"
        />
      </LogoContainer>
      <DetailsContainer>
        <Top>
          <div className="title">
            {data.name}
          </div>
          <div style={{fontSize: '12px', display: 'flex', columnGap: '20px'}}>
          </div>
        </Top>
        <Bottom>
          <div className="rating">
            <div className="rating-text">
              <Tag style={{margin: 0}} color={data.rating >= 4 ? 'green' : 'gold'}>
                {data.rating ? data.rating : 0}
              </Tag>
            </div>
            <Rate
              style={{fontSize: isSmallScreen ? '12px' : '16px'}}
              disabled
              allowHalf
              value={getRatingValue(data.rating)}
            />
          </div>
          <div className="reviews">
            <FontAwesomeIcon className="review-icon" icon={faCommentLines} />
            {data.reviewCount} Reviews
          </div>
        </Bottom>
        <QuoteCarousel items={data.reviews} />
        <div style={{fontSize: '12px', marginTop: '10px', marginBottom: '7px', color: '#5d5d5d'}}>
          Services
        </div>
        <div className="service-container">
          {data.services && data.services.map((service, index) => (
            <div key={index} className="service-tag">
              <div className="icon-container">
                <QualifiedServiceIcon.Regular serviceName={service} className="icon" />
              </div>
              <div className="text">{service}</div>
            </div>
          ))}
        </div>

      </DetailsContainer>
    </Container>
  );
};

SpotlightPremium.propTypes = {
  data: PropTypes.array,
  isLoading: PropTypes.bool,
};

const QuoteContainer = styled.div`
  font-family: 'Georgia', serif;
  background-color: #fdfcf9;
  color: #333;
  padding: 40px 20px 25px 20px;
  border-radius: 12px;
  width: 100%;
  margin-top: 10px;
  position: relative;
  text-align: center;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
  box-sizing: border-box;
  max-width: 280px;
  @media (max-width: ${style.sizes.large}) {
    max-width: 200px;
  }
  
  @media (max-width: ${style.sizes.small}) {
    max-width: 280px;
  }
`;

const QuoteMarkLeft = styled.span`
  font-size: 40px;
  color: #e0ddd8;
  position: absolute;
  top: 10px;
  left: 20px;
  font-family: serif;
`;

const QuoteMarkRight = styled.span`
  font-size: 40px;
  color: #e0ddd8;
  position: absolute;
  bottom: 10px;
  right: 20px;
  font-family: serif;
`;

const QuoteText = styled.p`
  font-size: 14px;
  line-height: 1.6;
  font-style: italic;
  margin: 0 auto;


  /* Ellipsis after 4 lines */
  display: -webkit-box;
  -webkit-line-clamp: 4;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Author = styled.p`
  margin-top: 15px;
  font-size: 14px;
  color: #777;
  font-style: normal;
  margin-bottom: 10px;
`;

const QuoteBlock = ({ content, author, rating}) => {
  return (
    <QuoteContainer>
      <QuoteMarkLeft>“</QuoteMarkLeft>
      <QuoteText>
        {content}
      </QuoteText>
      <Author>— {author}</Author>
      <div>
        <Rate
          style={{fontSize: '12px', color: '#bcb192'}}
          disabled
          allowHalf
          value={getRatingValue(rating)}
        />
      </div>
      <QuoteMarkRight>”</QuoteMarkRight>
    </QuoteContainer>
  );
};

QuoteBlock.propTypes = {
  content: PropTypes.string,
  author: PropTypes.string,
  rating: PropTypes.number,
};

const Dots = styled.div`
  display: flex;
  gap: 8px;
  justify-content: center;
  align-items: center;
  margin-top: 14px;
`;

const Dot = styled.button`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  border: 0;
  background: ${({ active }) => (active ? '#555' : '#d6d3cf')};
  cursor: pointer;
  padding: 0;
  transition: transform 160ms ease, background 160ms ease;
  &:hover { transform: scale(1.2); }
`;

const CarouselShell = styled.div`
  width: 100%;
  max-width: 100%;
  margin: 0 auto;
  overflow: hidden;
  position: relative;
  box-sizing: border-box;
  
  @media (max-width: ${style.sizes.small}) {
    max-width: 300px;
  }
`;

const Track = styled.div`
  display: flex;
  transition: transform 500ms ease;
  transform: ${({ index }) => `translateX(-${index * 100}%)`};
  will-change: transform;
`;

const Slide = styled.div`
  flex: 0 0 100%;
  min-width: 0; /* important so content can shrink within flex items */
  display: flex;
  justify-content: center; /* centers the card if you cap its width */
  align-items: stretch;
`;

const QuoteCarousel = ({
  items = [],
  intervalMs = REVIEW_DATA_INTERVAL,     // auto slide timing
  pauseOnHover = true,   // pause when the mouse is over
}) => {
  const [index, setIndex] = useState(0);
  const timerRef = useRef(null);
  const hoverRef = useRef(false);

  // Auto advance
  useEffect(() => {
    const start = () => {
      timerRef.current = setInterval(() => {
        if (!hoverRef.current && items.length > 0) {
          setIndex((i) => (i + 1) % items.length);
        }
      }, intervalMs);
    };
    start();
    return () => clearInterval(timerRef.current);
  }, [items.length, intervalMs]);

  const handleDot = (i) => setIndex(i);

  return (
    <div
      onMouseEnter={() => { if (pauseOnHover) hoverRef.current = true; }}
      onMouseLeave={() => { if (pauseOnHover) hoverRef.current = false; }}
    >
      <CarouselShell role="region" aria-roledescription="carousel">
        <Track index={index} role="listbox" aria-live="polite">
          {items.map((it, i) => (
            <Slide key={i} aria-label={`Slide ${i + 1} of ${items.length}`}>
              <QuoteBlock content={it.content} author={it.author} rating={it.rating} />
            </Slide>
          ))}
        </Track>
      </CarouselShell>

      <Dots aria-label="Carousel pagination">
        {items.map((_, i) => (
          <Dot
            key={i}
            active={i === index}
            onClick={() => handleDot(i)}
            aria-label={`Go to slide ${i + 1}`}
            aria-current={i === index ? 'true' : 'false'}
          />
        ))}
      </Dots>
    </div>
  );
};

QuoteCarousel.propTypes = {
  items: PropTypes.array,
  intervalMs: PropTypes.number,
  pauseOnHover: PropTypes.bool,
};

export default SpotlightPremium;
